// ─────────────────────────────────────────────────────────────────────────────
// OWN-AUTH SESSION LAYER (Faz 4)
//
// Firebase Auth → kendi .NET Auth API'mize (gts_auth_api) geçişin oturum katmanı.
// Bu modül erişim/yenileme token'larını ve User nesnesini localStorage'da tutar,
// token süresi dolmadan otomatik yeniler (rotation), ve AuthContext'in tepki
// verebilmesi için basit bir abonelik mekanizması (onAuthChange) sağlar.
//
// ⚠️ DAİRESEL İMPORT GÜVENLİĞİ:
//   Bu modül `config/apiClient`'ı TOP-LEVEL import ETMEZ. apiClient bizim
//   token okuma fonksiyonlarımıza ihtiyaç duyduğundan, döngüyü kırmak için
//   burada RAW axios + AUTH_API_URL sabiti kullanıyoruz. Böylece apiClient
//   güvenle `ownAuthSession`'ı import edebilir.
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios';

export const AUTH_API_URL =
  process.env.REACT_APP_AUTH_API_URL || 'https://gtsauthapi.aquasoft.com.tr';

// localStorage anahtarları
const ACCESS_TOKEN_KEY = 'gts_access_token';
const REFRESH_TOKEN_KEY = 'gts_refresh_token';
const AUTH_USER_KEY = 'gts_auth_user';
// Impersonation (süper admin → kullanıcı taklit) sırasında, geçiş yapılmadan ÖNCEKİ
// süper admin oturumunu burada yedekleriz; "Yöneticiye dön" ile geri yüklenir.
const IMP_BACKUP_KEY = 'gts_impersonation_backup';

/** Auth API'nin döndürdüğü User nesnesi (esnek — backend ek alan ekleyebilir). */
export interface OwnAuthUser {
  id: string;
  firebaseUid: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  companyId?: string;
  companyName?: string;
  branchesId?: string;
  employeeId?: string;
  photoURL?: string | null;
  [key: string]: unknown;
}

/** /login, /login-google, /login-apple, /refresh cevaplarının `Data` gövdesi. */
export interface OwnAuthData {
  AccessToken: string;
  RefreshToken: string;
  ExpiresIn?: number;
  User?: OwnAuthUser;
}

/** Tüm auth endpoint'lerinin ortak zarf (envelope) yapısı. */
interface AuthEnvelope<T> {
  Success: boolean;
  Message?: string;
  Data?: T;
}

// ── Yanıt casing normalizasyonu ──────────────────────────────────────────────
// gts_auth_api JSON çıktısı camelCase (ASP.NET Core varsayılanı: success/data/
// accessToken). Mobil OwnAuthSession her iki casing'i tolere ediyor; web de etmeli.
// Aksi halde sunucu 200 + başarılı dönse bile envelope.Success undefined kalır ve
// giriş "Kimlik doğrulama başarısız oldu." ile düşer.
function pickKey<T = unknown>(obj: unknown, ...keys: string[]): T | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const rec = obj as Record<string, unknown>;
  for (const k of keys) {
    if (rec[k] !== undefined && rec[k] !== null) return rec[k] as T;
  }
  return undefined;
}

/** Ham auth yanıtını (camelCase veya PascalCase) kanonik PascalCase zarfa çevirir. */
function normalizeAuthEnvelope(raw: unknown): AuthEnvelope<OwnAuthData> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const success = pickKey<boolean>(raw, 'Success', 'success');
  const message = pickKey<string>(raw, 'Message', 'message');
  const rawData = pickKey<unknown>(raw, 'Data', 'data');

  let data: OwnAuthData | undefined;
  if (rawData && typeof rawData === 'object') {
    data = {
      AccessToken: pickKey<string>(rawData, 'AccessToken', 'accessToken') ?? '',
      RefreshToken: pickKey<string>(rawData, 'RefreshToken', 'refreshToken') ?? '',
      ExpiresIn: pickKey<number>(rawData, 'ExpiresIn', 'expiresIn'),
      // User nesnesi her iki tarafta da camelCase (firebaseUid, email, ...).
      User: pickKey<OwnAuthUser>(rawData, 'User', 'user'),
    };
  }

  return { Success: success ?? false, Message: message, Data: data };
}

export type AuthChangeReason = 'login' | 'logout' | 'refresh' | 'refresh-failed';
export type AuthChangeListener = (reason: AuthChangeReason, user: OwnAuthUser | null) => void;

// ── Abonelik mekanizması ────────────────────────────────────────────────────
const listeners: AuthChangeListener[] = [];

/**
 * Oturum değişikliklerine abone ol (login/logout/refresh/refresh-failed).
 * AuthContext bunu kullanarak token yenileme hatasında state'i temizler.
 * @returns aboneliği iptal eden fonksiyon
 */
export function onAuthChange(cb: AuthChangeListener): () => void {
  listeners.push(cb);
  return () => {
    const idx = listeners.indexOf(cb);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

function emit(reason: AuthChangeReason, user: OwnAuthUser | null): void {
  // Kopyada gez — listener içinde unsubscribe edilse bile güvenli.
  for (const cb of [...listeners]) {
    try {
      cb(reason, user);
    } catch (e) {
      console.warn('[ownAuth] onAuthChange listener hatası:', e);
    }
  }
}

// ── Oturum kalıcılığı (localStorage) ─────────────────────────────────────────

function persistTokens(data: OwnAuthData): void {
  if (data.AccessToken) localStorage.setItem(ACCESS_TOKEN_KEY, data.AccessToken);
  if (data.RefreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, data.RefreshToken);
  if (data.User) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.User));
}

/** Tam oturumu (access + refresh + user) yazar. */
export function setSession(data: OwnAuthData): void {
  persistTokens(data);
}

/** Oturuma ait tüm anahtarları siler (impersonation yedeği dahil — tam çıkış). */
export function clearSession(): void {
  // Aktivite loglayıcıya haber ver: bekleyen loglar token silinmeden ÖNCE,
  // mevcut kimlikle keepalive gönderilsin (subAdminActivityLogger dinler).
  try {
    window.dispatchEvent(new Event('gts:session-clearing'));
  } catch {
    // window yoksa (test ortamı) sessizce geç.
  }
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(IMP_BACKUP_KEY);
}

/** localStorage'daki User nesnesini döndürür (yoksa null). */
export function getStoredUser(): OwnAuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OwnAuthUser;
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/** Mevcut kullanıcının kimlik anahtarı (firebaseUid). Firebase `auth.currentUser.uid` muadili. */
export function getCurrentUid(): string | null {
  const u = getStoredUser();
  return u ? (u.firebaseUid || u.id || null) : null;
}

/** Mevcut kullanıcının e-postası. */
export function getCurrentEmail(): string | null {
  const u = getStoredUser();
  return u ? (u.email ?? null) : null;
}

/** Mevcut (yenilenmemiş) access token — interceptor senkron okuma için. */
export function getStoredAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

// ── JWT decode (harici bağımlılık yok) ───────────────────────────────────────

/**
 * Own JWT (HMAC) payload'ını parse eder. İmza DOĞRULAMASI YAPMAZ — sadece
 * `exp`, `companyId`, `firebaseUid`, `role` gibi client-side ipuçlarını okur.
 */
export function decodeJwt(token: string | null | undefined): Record<string, any> | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padNeeded = payload.length % 4;
    if (padNeeded) payload += '='.repeat(4 - padNeeded);
    const decoded = atob(payload);
    // UTF-8 güvenli decode
    const json = decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isExpiredOrNearExpiry(token: string, skewSeconds = 60): boolean {
  const claims = decodeJwt(token);
  // exp okunamıyorsa token'ı geçerli kabul et (gereksiz refresh döngüsünü önle).
  if (!claims || typeof claims.exp !== 'number') return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return claims.exp - nowSec <= skewSeconds;
}

// ── Token yenileme (rotation + tek uçuşlu de-dup) ─────────────────────────────
let refreshPromise: Promise<string | null> | null = null;

function doRefresh(): Promise<string | null> {
  // Eşzamanlı çağrıları tek refresh isteğine indir.
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  refreshPromise = axios
    .post<AuthEnvelope<OwnAuthData>>(`${AUTH_API_URL}/api/auth/refresh`, { refreshToken })
    .then((res) => {
      const envelope = normalizeAuthEnvelope(res.data);
      const data = envelope?.Data;
      if (envelope?.Success && data?.AccessToken) {
        // Rotation: yeni access + refresh token'ı persist et.
        persistTokens(data);
        emit('refresh', data.User ?? getStoredUser());
        return data.AccessToken;
      }
      clearSession();
      emit('refresh-failed', null);
      return null;
    })
    .catch((err) => {
      console.warn('[ownAuth] Token yenileme başarısız:', err?.message);
      clearSession();
      emit('refresh-failed', null);
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Geçerli access token'ı döndürür. Süresi dolmuş veya 60 sn içinde dolacaksa
 * ve refresh token varsa otomatik yeniler. Yenileme hatasında oturumu temizler
 * ve `null` döner.
 */
export async function getAccessToken(): Promise<string | null> {
  const token = getStoredAccessToken();
  if (token && !isExpiredOrNearExpiry(token)) return token;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return token; // yenileyemiyoruz — eldeki (muhtemelen null) token

  return doRefresh();
}

/**
 * Yenilemeyi ZORLAR (eldeki token süresi dolmamış görünse bile). apiClient 401
 * retry akışı için. De-dup edilir.
 */
export function forceRefresh(): Promise<string | null> {
  return doRefresh();
}

// ── Login akışları ────────────────────────────────────────────────────────────

function handleAuthResponse(envelope: AuthEnvelope<OwnAuthData> | undefined): OwnAuthUser {
  if (!envelope?.Success || !envelope.Data?.AccessToken) {
    throw new Error(envelope?.Message || 'Kimlik doğrulama başarısız oldu.');
  }
  setSession(envelope.Data);
  const user = envelope.Data.User ?? getStoredUser();
  emit('login', user);
  if (!user) {
    throw new Error('Kimlik doğrulama başarılı ancak kullanıcı bilgisi alınamadı.');
  }
  return user;
}

export async function login(email: string, password: string): Promise<OwnAuthUser> {
  const res = await axios.post<AuthEnvelope<OwnAuthData>>(
    `${AUTH_API_URL}/api/auth/login`,
    { email, password }
  );
  return handleAuthResponse(normalizeAuthEnvelope(res.data));
}

export async function loginGoogle(idToken: string): Promise<OwnAuthUser> {
  const res = await axios.post<AuthEnvelope<OwnAuthData>>(
    `${AUTH_API_URL}/api/auth/login-google`,
    { idToken }
  );
  return handleAuthResponse(normalizeAuthEnvelope(res.data));
}

export async function loginApple(identityToken: string): Promise<OwnAuthUser> {
  const res = await axios.post<AuthEnvelope<OwnAuthData>>(
    `${AUTH_API_URL}/api/auth/login-apple`,
    { identityToken }
  );
  return handleAuthResponse(normalizeAuthEnvelope(res.data));
}

/** En iyi çaba ile sunucu tarafında refresh token'ı iptal eder, sonra yereli temizler. */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  try {
    if (refreshToken) {
      await axios.post(`${AUTH_API_URL}/api/auth/logout`, { refreshToken });
    }
  } catch (e) {
    console.warn('[ownAuth] /logout hatası (best-effort, yok sayıldı):', e);
  } finally {
    clearSession();
    emit('logout', null);
  }
}

// ── Impersonation (süper admin → kullanıcı taklit) ───────────────────────────

/** Yedeklenen süper admin oturumunun şekli. */
interface ImpersonationBackup {
  access: string | null;
  refresh: string | null;
  user: OwnAuthUser | null;
}

/**
 * Şu an bir impersonation (kullanıcı taklit) oturumunda mıyız?
 * Önce yedek varlığına, yoksa access token'daki `imp` claim'ine bakar.
 */
export function isImpersonating(): boolean {
  if (localStorage.getItem(IMP_BACKUP_KEY)) return true;
  const claims = decodeJwt(getStoredAccessToken());
  return claims?.imp === true || claims?.imp === 'true';
}

/**
 * Aktif impersonation hakkında özet bilgi: hedef (görüntülenen) kullanıcı ve
 * gerçek aktör (süper admin) e-postası. Impersonation yoksa null.
 */
export function getImpersonationInfo(): { targetUser: OwnAuthUser | null; actorEmail: string | null } | null {
  if (!isImpersonating()) return null;
  const claims = decodeJwt(getStoredAccessToken());
  let actorEmail: string | null = (claims?.act_email as string) ?? null;
  if (!actorEmail) {
    // Yedekten aktör e-postasını çek (token claim'i yoksa).
    try {
      const raw = localStorage.getItem(IMP_BACKUP_KEY);
      if (raw) {
        const b = JSON.parse(raw) as ImpersonationBackup;
        actorEmail = b.user?.email ?? null;
      }
    } catch {
      /* yok say */
    }
  }
  return { targetUser: getStoredUser(), actorEmail };
}

/**
 * Süper admin olarak hedef kullanıcıya geçer.
 * Mevcut (süper admin) oturumu yedeklenir, ardından /impersonate çağrısıyla hedef
 * kullanıcı için yeni token kurulur. Geçiş başarısızsa yedek geri yüklenmez (oturum
 * bozulmaz; eski oturum zaten yerinde kalır).
 */
export async function impersonate(target: { targetUserId?: number; targetEmail?: string }): Promise<OwnAuthUser> {
  const callerToken = await getAccessToken();
  if (!callerToken) {
    throw new Error('Oturum bulunamadı. Lütfen yeniden giriş yapın.');
  }

  // Zaten taklit oturumundaysak zincirlemeyi engelle.
  if (isImpersonating()) {
    throw new Error('Zaten bir kullanıcı taklit oturumundasınız. Önce yöneticiye dönün.');
  }

  // Mevcut süper admin oturumunu yedekle.
  const backup: ImpersonationBackup = {
    access: getStoredAccessToken(),
    refresh: getRefreshToken(),
    user: getStoredUser(),
  };

  const res = await axios.post<AuthEnvelope<OwnAuthData>>(
    `${AUTH_API_URL}/api/auth/impersonate`,
    { targetUserId: target.targetUserId, targetEmail: target.targetEmail },
    { headers: { Authorization: `Bearer ${callerToken}` } }
  );

  const envelope = normalizeAuthEnvelope(res.data);
  if (!envelope?.Success || !envelope.Data?.AccessToken) {
    throw new Error(envelope?.Message || 'Kullanıcı oturumuna geçilemedi.');
  }

  // Yedeği SADECE geçiş başarılı olunca yaz (aksi halde eski oturum bozulmaz).
  localStorage.setItem(IMP_BACKUP_KEY, JSON.stringify(backup));

  setSession(envelope.Data);
  const user = envelope.Data.User ?? getStoredUser();
  emit('login', user);
  if (!user) {
    throw new Error('Geçiş başarılı ancak kullanıcı bilgisi alınamadı.');
  }
  return user;
}

/**
 * Impersonation'ı sonlandırır ve yedeklenen süper admin oturumunu geri yükler.
 * Yedek yoksa null döner (zaten normal oturum).
 */
export function stopImpersonation(): OwnAuthUser | null {
  const raw = localStorage.getItem(IMP_BACKUP_KEY);
  if (!raw) return null;
  try {
    const b = JSON.parse(raw) as ImpersonationBackup;
    setSession({
      AccessToken: b.access ?? '',
      RefreshToken: b.refresh ?? '',
      User: b.user ?? undefined,
    });
    localStorage.removeItem(IMP_BACKUP_KEY);
    emit('login', b.user ?? null);
    return b.user ?? null;
  } catch {
    localStorage.removeItem(IMP_BACKUP_KEY);
    return null;
  }
}

/** İsteğe bağlı: token'ın sunucu tarafında hâlâ geçerli olup olmadığını doğrular. */
export async function validate(token?: string): Promise<boolean> {
  const t = token || getStoredAccessToken();
  if (!t) return false;
  try {
    const res = await axios.post<AuthEnvelope<unknown>>(
      `${AUTH_API_URL}/api/auth/validate-token`,
      { token: t }
    );
    return !!normalizeAuthEnvelope(res.data)?.Success;
  } catch {
    return false;
  }
}

const ownAuthSession = {
  AUTH_API_URL,
  setSession,
  clearSession,
  getStoredUser,
  getCurrentUid,
  getCurrentEmail,
  getRefreshToken,
  getStoredAccessToken,
  decodeJwt,
  getAccessToken,
  forceRefresh,
  login,
  loginGoogle,
  loginApple,
  logout,
  validate,
  onAuthChange,
  impersonate,
  stopImpersonation,
  isImpersonating,
  getImpersonationInfo,
};

export default ownAuthSession;
